import { useEffect, useRef, useState } from "react";
import RedemptionImpact from "@/components/redemption/RedemptionImpact";
import { GfgButton } from "@/components/ui/GfgButton";
import { ACK_HOLD_MS, GIFT_ENVELOPE_ICON, MESSAGE_FLOURISH_VIDEO } from "@/constants/redemption";
import { useSendThankYou } from "@/hooks/useSendThankYou";
import type { Product } from "@/interfaces/product";
import type { GiftContext } from "@/interfaces/redemption";
import { giftKind, senderGreeting } from "@/utils/redemption-helper";

/** ask → write a note; ack → brief "note sent" beat; impact → the closing reveal. */
type Phase = "ask" | "ack" | "impact";

// Soft light halo so the ask copy stays crisp over the flourish (prototype parity).
// Kept local: a component-scoped Tailwind class fragment, not shared domain data.
const HALO = "[text-shadow:0_1px_2px_rgba(251,249,247,0.9),0_0_16px_rgba(251,249,247,0.85)]";

interface RedemptionSuccessProps {
  /** Resolved gift context — its sender drives the "leave a message" ask. */
  gift: GiftContext;
  /** Redeem token — the credential for the save-note API. */
  token: string;
  /** The contact just claimed by this redemption (from the redeem response). Required for a
   *  Universal Link, whose shared token has no bound contact to attribute the note to. */
  contactId?: number;
  /** The chosen gift — its type tunes the "on its way" copy + the impact reveal. */
  product?: Product;
  /** Recipient's first name — personalizes the closing impact headline (RFW-10). */
  recipientFirstName?: string;
}

/**
 * Redeemed terminus — "Your gift is on its way!" + the thank-you note ask (RFW-9).
 *
 * Our Tailwind twin of the prototype's message stage (the `/dev/redemption`
 * `RedemptionMessage` "ask"): a warm clear-dust surface with a full-bleed flourish
 * video behind a Crimson display title, a dropping italic subtitle, and a white
 * note card. The recipient leaves a note for the sender (saved via
 * `POST /:token/message` → `contacts.thank_note`) or skips, then the closing impact
 * reveal takes over (`RedemptionImpact`, RFW-10).
 */
export default function RedemptionSuccess({
  gift,
  token,
  contactId,
  product,
  recipientFirstName,
}: RedemptionSuccessProps) {
  const [note, setNote] = useState("");
  const [phase, setPhase] = useState<Phase>("ask");
  const send = useSendThankYou();
  const [prefersReduced] = useState(
    () =>
      typeof window !== "undefined" &&
      !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches,
  );
  const videoRef = useRef<HTMLVideoElement>(null);

  // Reliable muted autoplay — React's `muted` prop can be dropped from the DOM node,
  // so set it explicitly before play() (matches the prototype's flourish handling).
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = true;
    v.play().catch(() => {
      /* autoplay blocked — leave the box off-screen, no flourish */
    });
  }, []);

  const isCharity = product ? giftKind(product) === "charity" : false;
  const title = isCharity ? "Your gift is on its way to Do Good!" : "Your gift is on its way!";
  const subtitle = isCharity ? null : "Doing Good as it goes…";
  const greeting = senderGreeting(gift.sender);
  const hasNamedSender = greeting !== "the sender";
  const senderName =
    (gift.sender.firstName ?? "").trim() || (gift.sender.companyName ?? "").trim() || "the sender";

  // Skip / empty send → straight to the impact reveal (no note to save). A real note →
  // save it, then briefly acknowledge before the reveal takes over.
  const onSend = () => {
    const trimmed = note.trim();
    if (!trimmed) {
      setPhase("impact");
      return;
    }
    send.mutate({ token, note: trimmed, contactId }, { onSuccess: () => setPhase("ack") });
  };

  // Hold the "note sent" acknowledgement for a beat, then reveal the impact (RFW-10).
  useEffect(() => {
    if (phase !== "ack") return;
    const t = window.setTimeout(() => setPhase("impact"), ACK_HOLD_MS);
    return () => window.clearTimeout(t);
  }, [phase]);

  // Note sent (or skipped) → the closing impact reveal takes over the screen.
  if (phase === "impact") {
    return <RedemptionImpact product={product} recipientFirstName={recipientFirstName} />;
  }

  return (
    <div className="absolute inset-0 z-10310 flex flex-col items-center overflow-y-auto bg-clear-dust justify-center-safe motion-safe:animate-tail-in">
      {/* Celebratory flourish behind the note — full-bleed, quiet, plays once. Shown
          only during the ask (gone by the acknowledgement), and skipped under
          reduced-motion (prototype parity). */}
      {phase === "ask" && !prefersReduced && (
        <video
          ref={videoRef}
          className="pointer-events-none fixed inset-0 z-0 h-full w-full object-cover object-center opacity-50"
          src={MESSAGE_FLOURISH_VIDEO}
          muted
          playsInline
          autoPlay
          preload="auto"
        >
          <track kind="captions" />
        </video>
      )}

      <div className="relative z-2 w-full max-w-140 px-8 py-12 text-center max-sm:px-5.5 max-sm:py-10">
        {phase === "ack" ? (
          <>
            <img
              className="mx-auto mb-4 block h-14 w-14 object-contain"
              src={GIFT_ENVELOPE_ICON}
              alt=""
            />
            <p className="mx-auto max-w-[22ch] text-balance font-crimson text-[clamp(24px,3.6vw,30px)] leading-[1.3] text-rodeo-black">
              Thanks — we'll make sure {senderName} gets your message.
            </p>
          </>
        ) : (
          <>
            <h1
              className={`mb-1.5 text-balance font-crimson font-normal text-[clamp(38px,6vw,56px)] leading-[1.08] text-rodeo-black max-sm:text-[clamp(30px,8vw,42px)] ${HALO}`}
            >
              {title}
            </h1>
            {subtitle && (
              // The wrap clips; the subtitle starts tucked above the title, then drops in.
              <div className="mb-6.5 overflow-hidden py-0.5">
                <p
                  className={`font-crimson text-[clamp(21px,3vw,27px)] italic leading-[1.3] text-dust-ink motion-safe:animate-subtitle-drop max-sm:text-[clamp(19px,4.6vw,24px)] ${HALO}`}
                >
                  {subtitle}
                </p>
              </div>
            )}
            <h2
              className={`mb-5 font-lato text-[19px] leading-[1.35] text-graphite max-sm:mb-4.5 max-sm:text-[17px] ${HALO}`}
            >
              Would you like to leave a message for {greeting}?
            </h2>

            {/* The prototype's "letter" — a warm white card so leaving a note feels like
                penning a card, not filling a form field. */}
            <div className="mb-5.5 rounded-2xl border border-card-border bg-white px-5.5 pt-5.5 pb-5 text-left shadow-[0_12px_34px_rgba(51,40,32,0.06)] max-sm:px-4.5 max-sm:pt-4.5 max-sm:pb-4">
              {hasNamedSender && (
                <p className="mb-2.5 font-crimson text-[18px] italic leading-[1.3] text-dust-ink">
                  To {greeting},
                </p>
              )}
              <textarea
                aria-label="Message to the sender"
                placeholder="Write your note…"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                className="min-h-33 w-full resize-y border-0 bg-transparent p-0 text-left font-lato text-[16px] leading-[1.6] text-rodeo-black outline-none placeholder:text-graphite max-sm:min-h-27.5"
              />
            </div>

            {send.isError && (
              <p className="mb-3 font-lato text-[13px] text-good-wine">
                Couldn't send your message. Please try again.
              </p>
            )}

            <div className="flex flex-wrap items-center justify-center gap-2">
              <GfgButton
                variant="primary"
                onClick={onSend}
                disabled={send.isPending}
                className="max-sm:min-h-11"
              >
                {send.isPending ? "Sending…" : "Send message"}
              </GfgButton>
              <GfgButton
                variant="text"
                onClick={() => setPhase("impact")}
                disabled={send.isPending}
                className="max-sm:-my-3 max-sm:py-3 max-sm:-mx-2 max-sm:px-2"
              >
                SKIP
              </GfgButton>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
